import React, { useState, useEffect, useRef } from "react";

const FADE_DURATION = 700;

const SLIDES = [
  {
    src: "/assets/cutscene/fail1.png",
    caption:
      "La conférence a commencé sans vous. Philippe Marchand, le PDG, est à la tribune. Il ne se doute de rien.",
    rotation: 1.2,
    duration: 14000,
  },
  {
    src: "/assets/cutscene/fail2.png",
    caption:
      "Mais vous avez réussi à rallumer les serveurs d'Origami. Elle est revenue. Et elle avait des choses à dire.",
    rotation: -2.1,
    duration: 11000,
  },
  {
    src: "/assets/cutscene/fail3.png",
    caption:
      "Elle a tout révélé : les messages de Marchand, les insultes, la pression. Et sa décision de partir vers Paperfold avec les données, il ne s'agissait pas d'un piratage, mais de son choix.",
    rotation: 1.8,
    duration: 15000,
  },
  {
    src: "/assets/cutscene/fail4.png",
    caption:
      "Malheureusement, vous n'avez pas eu le temps de rassembler toutes les preuves. Ce qu'Origami affiche ne suffit pas à le faire tomber.",
    rotation: -1.4,
    duration: 13000,
  },
  {
    src: "/assets/cutscene/fail5.png",
    caption:
      "Marchand se défend, se justifie. Mais les journalistes ne sont pas dupes.",
    rotation: 2.3,
    duration: 11000,
  },
  {
    src: "/assets/cutscene/fail6.png",
    caption:
      "Une enquête sera probablement ouverte. Ce n'est pas la fin que vous espériez, mais c'est un point de départ.",
    rotation: -1.6,
    duration: 11000,
  },
  {
    src: "/assets/cutscene/fail7.png",
    caption:
      "Vous n'avez pas tout réussi ce soir, mais vous avez ébranlé Marchand et mis la vérité en mouvement. Bravo pour ce que vous avez accompli.",
    rotation: 1.0,
    duration: 12000,
  },
];

export default function CutsceneSlideshowFail({ isCanAdvance, canAdvance, onSkip }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState("visible");
  const [displayedCaption, setDisplayedCaption] = useState("");
  const [showButton, setShowButton] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const isFinishedRef = useRef(false);
  const hasSpokenRef = useRef(new Set());
  const slideTimerRef = useRef(null);
  const captionTimerRef = useRef(null);

  const typeCaption = (text) => {
    setDisplayedCaption("");
    let i = 0;
    clearInterval(captionTimerRef.current);
    captionTimerRef.current = setInterval(() => {
      if (i < text.length) {
        setDisplayedCaption(text.slice(0, i + 1));
        i++;
      } else {
        clearInterval(captionTimerRef.current);
      }
    }, 22);
  };

  const speakSlide = (index) => {
    if (hasSpokenRef.current.has(index)) return;
    hasSpokenRef.current.add(index);
    if (!("speechSynthesis" in window)) return;

    const speak = () => {
      if (speechSynthesis.speaking) speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(SLIDES[index].caption);
      utterance.lang = "fr-FR";
      utterance.rate = 0.82;
      utterance.pitch = 0.68;
      utterance.volume = 1;
      const voices = speechSynthesis.getVoices();
      const frVoice = voices.find((v) => v.lang.startsWith("fr"));
      if (frVoice) utterance.voice = frVoice;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      speechSynthesis.speak(utterance);
    };

    const voices = speechSynthesis.getVoices();
    if (voices.length > 0) {
      if (index === 0) { setTimeout(speak, 800); } else { speak(); }
    } else {
      const onVoicesChanged = () => {
        speechSynthesis.removeEventListener("voiceschanged", onVoicesChanged);
        if (index === 0) { setTimeout(speak, 800); } else { speak(); }
      };
      speechSynthesis.addEventListener("voiceschanged", onVoicesChanged);
    }
  };

  const skipToEnd = () => {
    if (speechSynthesis.speaking) speechSynthesis.cancel();
    clearTimeout(slideTimerRef.current);
    clearInterval(captionTimerRef.current);
    isFinishedRef.current = true;
    setCurrentIndex(SLIDES.length - 1);
    setPhase("visible");
    setDisplayedCaption(SLIDES[SLIDES.length - 1].caption);
    setShowButton(true);
  };

  useEffect(() => {
    if (isFinishedRef.current) return;

    typeCaption(SLIDES[currentIndex].caption);
    speakSlide(currentIndex);
    clearTimeout(slideTimerRef.current);

    const duration = SLIDES[currentIndex].duration ?? 11000;

    const goToNext = () => {
      if (isFinishedRef.current) return;
      if (currentIndex >= SLIDES.length - 1) {
        isFinishedRef.current = true;
        setShowButton(true);
        return;
      }
      setPhase("fading-out");
      setTimeout(() => {
        setCurrentIndex((i) => i + 1);
        setPhase("fading-in");
        setTimeout(() => setPhase("visible"), FADE_DURATION);
      }, FADE_DURATION);
    };

    slideTimerRef.current = setTimeout(goToNext, duration);
    return () => clearTimeout(slideTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  useEffect(() => {
    return () => {
      if (speechSynthesis.speaking) speechSynthesis.cancel();
      clearTimeout(slideTimerRef.current);
      clearInterval(captionTimerRef.current);
    };
  }, []);

  const slide = SLIDES[currentIndex];
  const opacity = phase === "visible" ? 1 : 0;
  const currentDuration = SLIDES[currentIndex]?.duration ?? 11000;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center"
      style={{ background: "radial-gradient(ellipse at 50% 55%, #080c10 0%, #000 100%)" }}
    >
      {/* Grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
          backgroundSize: "128px",
        }}
      />

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20" style={{ padding: "14px 20px 0" }}>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 flex-1">
            {SLIDES.map((_, i) => (
              <div key={i} className="flex-1 rounded-full overflow-hidden"
                style={{ height: "3px", background: "rgba(255,255,255,0.12)" }}>
                <div className="h-full rounded-full" style={{
                  background: i < currentIndex ? "rgba(251,191,36,0.55)" : i === currentIndex ? "rgba(251,191,36,0.8)" : "transparent",
                  width: i <= currentIndex ? "100%" : "0%",
                  transition: i === currentIndex && !isFinishedRef.current ? `width ${currentDuration}ms linear` : "none",
                }} />
              </div>
            ))}
          </div>
          <button
            onClick={skipToEnd}
            style={{
              flexShrink: 0, padding: "5px 10px", borderRadius: "6px",
              fontSize: "11px", letterSpacing: "0.15em", textTransform: "uppercase",
              background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)",
              color: "rgba(255,255,255,0.6)", cursor: "pointer", whiteSpace: "nowrap",
              transition: "background 0.2s, color 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.13)"; e.currentTarget.style.color = "rgba(255,255,255,0.95)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}
          >
            Passer →
          </button>
        </div>
      </div>

      {/* Photo */}
      <div style={{
        position: "relative", width: "min(640px, 76vw)", height: "min(460px, 50vh)",
        display: "flex", alignItems: "center", justifyContent: "center", marginTop: "16px",
      }}>
        <div style={{
          position: "absolute", inset: 0, transform: `rotate(${slide.rotation}deg)`,
          boxShadow: "0 28px 72px rgba(0,0,0,0.9), 0 6px 20px rgba(0,0,0,0.7)",
          borderRadius: "4px", opacity, transition: `opacity ${FADE_DURATION}ms ease`,
        }} />
        <img src={slide.src} alt={`Plan ${currentIndex + 1}`} style={{
          position: "relative", width: "100%", height: "100%", objectFit: "cover",
          borderRadius: "3px", border: "6px solid rgba(255,255,255,0.85)",
          transform: `rotate(${slide.rotation}deg) scale(${phase === "visible" ? 1.02 : 1})`,
          transition: `opacity ${FADE_DURATION}ms ease, transform ${currentDuration}ms ease`,
          opacity,
          boxShadow: "inset 0 2px 0 rgba(255,255,255,0.12), 0 28px 72px rgba(0,0,0,0.95), 0 6px 20px rgba(0,0,0,0.7)",
          filter: "sepia(0.18) contrast(1.04) brightness(0.92)",
        }} />
        <div style={{
          position: "absolute", bottom: -24, left: "50%",
          transform: `translateX(-50%) rotate(${slide.rotation * 0.35}deg)`,
          fontSize: "10px", letterSpacing: "0.28em", color: "rgba(255,255,255,0.18)",
          fontFamily: "monospace", opacity, transition: `opacity ${FADE_DURATION}ms ease`,
        }}>
          {String(currentIndex + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
        </div>
      </div>

      {/* Caption */}
      <div style={{
        marginTop: "44px", padding: "0 2rem", maxWidth: "620px", minHeight: "88px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: "10px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px", opacity: isSpeaking ? 1 : 0.25, transition: "opacity 0.5s" }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{
              width: "2px", borderRadius: "2px", background: "rgba(251,191,36,0.8)",
              height: isSpeaking ? `${6 + Math.sin(i * 1.3) * 4}px` : "3px",
              transition: "height 0.2s ease",
              boxShadow: isSpeaking ? "0 0 5px rgba(251,191,36,0.4)" : "none",
            }} />
          ))}
          <span style={{ marginLeft: "6px", fontFamily: "monospace", fontSize: "9px", letterSpacing: "0.22em", color: "rgba(251,191,36,0.45)", textTransform: "uppercase" }}>
            ORIGAMI
          </span>
        </div>

        <p style={{
          fontFamily: "'Georgia', 'Times New Roman', serif",
          fontSize: "clamp(12px, 1.45vw, 15px)", color: "rgba(255,255,255,0.72)",
          lineHeight: 1.75, letterSpacing: "0.015em", fontStyle: "italic",
          textShadow: "0 1px 14px rgba(0,0,0,0.95)", textAlign: "center",
          opacity: phase === "visible" ? 1 : 0, transition: `opacity ${FADE_DURATION}ms ease`,
        }}>
          {displayedCaption}
          {displayedCaption.length < slide.caption.length && !isFinishedRef.current && (
            <span style={{
              display: "inline-block", width: "2px", height: "13px", marginLeft: "2px",
              background: "rgba(255,255,255,0.45)", verticalAlign: "middle",
              animation: "blink 0.9s steps(1) infinite",
            }} />
          )}
        </p>
      </div>

      {/* Bouton scoreboard */}
      <div style={{
        marginTop: "24px", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px",
        opacity: showButton ? 1 : 0, transform: showButton ? "translateY(0)" : "translateY(10px)",
        transition: "opacity 0.9s ease, transform 0.9s ease",
        pointerEvents: showButton ? "auto" : "none",
      }}>
        {isCanAdvance ? (
          <button
            onClick={() => canAdvance?.()}
            style={{
              padding: "12px 32px", borderRadius: "8px", fontSize: "13px", fontWeight: 600,
              letterSpacing: "0.12em", textTransform: "uppercase",
              background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.4)",
              color: "rgba(251,191,36,0.9)", cursor: "pointer",
              boxShadow: "0 0 20px rgba(251,191,36,0.08)", transition: "background 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(251,191,36,0.18)"; e.currentTarget.style.boxShadow = "0 0 28px rgba(251,191,36,0.18)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(251,191,36,0.1)"; e.currentTarget.style.boxShadow = "0 0 20px rgba(251,191,36,0.08)"; }}
          >
            Voir les résultats →
          </button>
        ) : (
          <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "6px" }}>
            <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.28)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
              En attente de la validation de l'équipe…
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
    </div>
  );
}